import type { RuntimeConfig } from '@nuxt/schema';
import { useAuthStore } from '~/stores/auth';
import type { APIErrorResponse, FetchParams } from '~/interfaces/api/main';
import type { ApiResponse } from '~/types/main';
import { getArcSheetHeaders, getAuthHeaders, normalizeApiError, normalizeJsonApiResponse } from '~/composables/http/utils';

export const useAuthFetchNext = async (
  url: string,
  params?: FetchParams,
): Promise<ApiResponse> => {
  const authStore = useAuthStore();
  const config: RuntimeConfig = useRuntimeConfig();

  let transactionId = null;
  let statusCode = null;

  const authHeaders = await getAuthHeaders();
  const headers: Record<string, string> = {
    ...(params?.headers || {}),
    ...getArcSheetHeaders(),
    ...authHeaders,
  };

  // Options propres au wrapper : ne doivent pas etre transmises a useFetch.
  const { silentError, ...fetchParams } = params ?? {};

  const opts: FetchParams = {
    baseURL: config.public.API_BASE_URL,
    headers,
    ...fetchParams,
    onRequest ({ options }) {
      // eslint-disable-next-line no-param-reassign
      options.headers = headers;
    },
    onResponse ({ response }) {
      transactionId = response.headers.get('X-Transaction-Id') || null;
      statusCode = response.status;
      return response._data;
    },
  };

  try {
    const { data, error } = await useFetch<ApiResponse>(url, opts);

    if (error.value?.statusCode === 401) {
      await authStore.fetchToken();
    }

    if (statusCode === 204) {
      return {
        transactionId,
        errors: [],
        statusCode: 204,
      };
    }

    if (error.value) {
      return handleErrorResponse(error);
    }

    if (data.value) {
      if (isJsonApiResponse(data.value)) {
        return normalizeJsonApiResponse(data.value);
      }
    }

    return {
      transactionId,
      errors: [
        {
          title: 'Format de données non reconnu',
          detail: 'Les données reçues ne sont pas au format attendu.',
        },
      ],
    } as APIErrorResponse;
  } catch (error) {
    return handleErrorResponse(error);
  }

  function handleErrorResponse (error: any): APIErrorResponse {
    if (!silentError) {
      useToastService().add({
        severity: 'error',
        summary: error?.value?.data?.title || 'Goldorac API Request Failed',
        detail: error?.value?.data?.detail || error?.value?.message || 'Unknown error',
        life: 3000,
      });
    }

    return normalizeApiError(
      error.value?.data,
      error.value?.statusCode ?? 500,
      transactionId,
      error?.value?.title,
    );
  }
};
